import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type ReactNode
} from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AuthContext, type AuthUser } from './useAuth';
import {
    getToken,
    setToken,
    clearToken,
    subscribeToken,
    authFetch,
    fetchAs
} from '../../utils/authFetch';
import {
    subscribeSyncBlockers,
    getSyncBlockers,
    clearSyncBlocker,
    type SyncBlocker
} from '../../db/syncBlockers';
import { setReplicationAuthActive } from '../../db/replication';
import {
    cacheOfflineCredential,
    loadOfflineCredential
} from './offlineCredential';
import { keepCredential } from '../../utils/pendingCredentials';
import {
    setCurrentAuthor,
    hasPendingChanges,
    pendingCountsByAuthor
} from '../../db/authorJournal';
import { takeNfcTokenFromUrl, tokenFromHash } from './nfcLogin';
import { onOpenedWithUrl } from '../../lib/nativeApp';
import {
    getImpersonation,
    requestImpersonationToken,
    setImpersonation,
    type Impersonation
} from './impersonation';
import { formatDate } from '../../utils/formatDate';
import LoginPage from './LoginPage';
import ReLoginDialog from './ReLoginDialog';
import SwitchUserDialog from './SwitchUserDialog';
import SyncLoginDialog from './SyncLoginDialog';
import TopEdgeBanner from '../../components/TopEdgeBanner';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { jwtPayload } from '../../utils/jwtPayload';

const REFRESH_INTERVAL = 6 * 60 * 60 * 1000;

/**
 * Outcome of a token refresh. Crucially, a transient failure (network blip,
 * aborted request, server hiccup, or offline) is NOT the same as a definitive
 * rejection — only the latter should bounce the user to re-login.
 */
type RefreshOutcome = 'ok' | 'expired' | 'transient';

/**
 * One in-flight refresh at a time, shared across every caller. The refresh
 * endpoint REVOKES the presented token and issues a new one, so two concurrent
 * refreshes with the same token would make the second 401 for no real reason
 * (React StrictMode fires the initial refresh twice in dev; a timer + a reload
 * can also overlap). Coalescing them into a single request avoids that race.
 */
let inFlightRefresh: Promise<RefreshOutcome> | null = null;

// Login failures from the backend carry a stable `code`; map it to a localized
// message (falling back to the server's English `error`, then a generic one).
const AUTH_ERROR_CODE_TO_ID: Record<string, string> = {
    missingCredentials: 'auth.error.missingCredentials',
    invalidCredentials: 'auth.error.invalidCredentials',
    accountDisabled: 'auth.error.accountDisabled',
    noRoles: 'auth.error.noRoles',
    permissionDenied: 'auth.error.permissionDenied',
    twoFactorRequired: 'auth.error.twoFactorRequired',
    unreachable: 'auth.error.unreachable',
    unexpectedAnswer: 'auth.error.unexpectedAnswer',
    failed: 'auth.error.failed',
    // A dead bearer token, worded for a SESSION that ended. The card and QR
    // path words the same three codes differently — see `carrierErrorId`.
    tokenExpired: 'auth.error.sessionExpired',
    tokenRevoked: 'auth.error.sessionRevoked',
    tokenUnknown: 'auth.error.sessionUnknown'
};

/**
 * What to tell somebody whose CARD or printed code was just refused. The server
 * says which way it failed, and the three answers lead somewhere different: an
 * expired card is extended from the office without being handed in, a revoked
 * one is finished for good, and an unknown one never belonged here.
 */
function carrierErrorId(code: string | undefined, card: boolean): string {
    if (code === 'tokenExpired') {
        return card ? 'auth.error.cardExpired' : 'auth.error.codeExpired';
    }
    if (code === 'tokenRevoked') {
        return card ? 'auth.error.cardRevoked' : 'auth.error.codeRevoked';
    }
    if (code === 'tokenUnknown') {
        return card ? 'auth.error.cardUnknown' : 'auth.error.codeUnknown';
    }
    return 'auth.error.qrInvalid';
}

/** The rejections that are about the ACCOUNT, not about the credential. */
const ACCOUNT_ERROR_CODES: Record<string, string> = {
    accountDisabled: 'auth.error.accountDisabled',
    noRoles: 'auth.error.noRoles'
};

/** Whether `token` is an administrator's "Anmelden als" session. */
function isImpersonationToken(token: string | null): boolean {
    if (!token) {
        return false;
    }
    return Boolean(jwtPayload(token)?.impersonatorId);
}

/**
 * The administrator's way back, if this device is in an "Anmelden als" session.
 * A slot whose session is gone — signed out some other way — is dropped, or the
 * banner would offer a way back from somewhere nobody is.
 */
function initialImpersonation(): Impersonation | null {
    const impersonation = getImpersonation();
    if (impersonation && !isImpersonationToken(getToken())) {
        setImpersonation(null);
        return null;
    }
    return impersonation;
}

/** Best-effort user identity decoded from a stored JWT (no signature check). */
function userFromToken(token: string): AuthUser | null {
    const payload = jwtPayload(token);
    if (!payload) {
        return null;
    }
    return {
        id: typeof payload.userId === 'string' ? payload.userId : '',
        username: String(payload.username ?? ''),
        displayName: String(payload.username ?? ''),
        isAdmin: payload.isAdmin === true
    };
}

/**
 * The card's person when somebody ELSE is signed in on this device — then the
 * card has to be confirmed first: a card in a pocket brushes a phone easily,
 * and the person being signed out may have unsynced work.
 */
function otherPersonsCard(cardToken: string): AuthUser | null {
    const stored = getToken();
    const outgoing = stored ? userFromToken(stored) : null;
    const incoming = userFromToken(cardToken);
    if (outgoing && incoming && outgoing.id && outgoing.id !== incoming.id) {
        return incoming;
    }
    return null;
}

export default function AuthProvider({ children }: { children: ReactNode }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);
    // Why the session ended, when the server said (the access gate rejects a
    // disabled/role-less account with a `code`). Null = it simply expired.
    const [sessionEndCode, setSessionEndCode] = useState<string | null>(null);
    // Why a tapped NFC login card was refused, so the login screen can say so
    // instead of appearing for no visible reason.
    const [cardLoginError, setCardLoginError] = useState<string | null>(null);
    // A card tapped while somebody else is signed in — held until they confirm.
    const [pendingCard, setPendingCard] = useState<{
        token: string;
        incoming: AuthUser;
    } | null>(null);
    // Users whose queued changes the push is holding back until they sign in.
    const [blockers, setBlockers] = useState<SyncBlocker[]>([]);
    // "Later" on the prompt — asked again after a reload, not every retry.
    const [dismissedBlockers, setDismissedBlockers] = useState<string[]>([]);
    const [impersonation] = useState(initialImpersonation);
    const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

    const attemptRefresh = useCallback((): Promise<RefreshOutcome> => {
        // Reuse an already-running refresh rather than starting a second one.
        if (inFlightRefresh) {
            return inFlightRefresh;
        }
        inFlightRefresh = (async (): Promise<RefreshOutcome> => {
            try {
                const res = await authFetch('/api/auth/refresh', {
                    method: 'POST'
                });
                if (res.ok) {
                    const data = await res.json();
                    setToken(data.token);
                    /**
                     * The id is required so the ability can look up this user's
                     * profile (roles) after a reload. Use the id the server
                     * returns — it derives it from the verified token / DB
                     * session, the single source of truth (don't trust a
                     * client-decoded JWT).
                     */
                    setUser((prev) => ({
                        id: data.id || prev?.id || '',
                        username: data.username,
                        displayName: prev?.displayName || data.username,
                        isAdmin: data.isAdmin === true
                    }));
                    setSessionExpired(false);
                    setSessionEndCode(null);
                    return 'ok';
                }
                // Only a definitive auth rejection means the session is really
                // gone. Any other status (5xx, proxy error, …) is transient.
                if (res.status === 401) {
                    // The access gate says WHY it rejected (noRoles /
                    // accountDisabled); keep it so the re-login dialog can state
                    // the real reason instead of claiming the session expired.
                    const data = await res.json().catch(() => null);
                    setSessionEndCode(
                        typeof data?.code === 'string' ? data.code : null
                    );
                    return 'expired';
                }
                return 'transient';
            } catch {
                // Network error or an aborted request (e.g. a navigation
                // cancelling the fetch) — transient, never a reason to log out.
                return 'transient';
            }
        })().finally(() => {
            inFlightRefresh = null;
        });
        return inFlightRefresh;
    }, []);

    const refreshToken = useCallback(async (): Promise<RefreshOutcome> => {
        let outcome = await attemptRefresh();
        // Give a transient failure a couple of quick retries before giving up —
        // but never retry (or wait on) a definitive 401.
        for (
            let attempt = 0;
            outcome === 'transient' && attempt < 2 && navigator.onLine;
            attempt++
        ) {
            await new Promise((resolve) =>
                setTimeout(resolve, 400 * (attempt + 1))
            );
            outcome = await attemptRefresh();
        }
        return outcome;
    }, [attemptRefresh]);

    const loginWithToken = useCallback(
        async (token: string): Promise<string | null> => {
            // The scanned token is already a valid bearer JWT — store it, then
            // confirm it with the server and resolve the user identity.
            // A refused card must not cost whoever is signed in their session:
            // on a switch between people, theirs is the token it overwrote.
            const previousToken = getToken();
            const restorePreviousToken = () => {
                if (previousToken) {
                    setToken(previousToken);
                    return;
                }
                clearToken();
            };
            setToken(token);
            try {
                const res = await authFetch('/api/auth/me');
                if (!res.ok) {
                    restorePreviousToken();
                    if (res.status !== 401) {
                        // Not a verdict on the card at all: a gateway error, or
                        // the backend restarting. This used to read as "QR code
                        // invalid" and sent people looking for a fault in a
                        // perfectly good card — the next tap usually works.
                        return intl.formatMessage({
                            id: 'auth.error.loginRetry'
                        });
                    }
                    const data = await res.json().catch(() => null);
                    // A disabled/role-less account is rejected here too (the
                    // access gate runs on every request, not just login) — say
                    // which it is rather than blaming the card.
                    const accountId = data?.code
                        ? ACCOUNT_ERROR_CODES[data.code]
                        : undefined;
                    if (accountId) {
                        return intl.formatMessage({ id: accountId });
                    }
                    return intl.formatMessage(
                        {
                            id: carrierErrorId(data?.code, data?.card === true)
                        },
                        { date: formatDate(data?.expiresAt) }
                    );
                }
                const data = await res.json();
                setUser({
                    id: data.id,
                    username: data.username,
                    displayName: data.username,
                    isAdmin: data.isAdmin === true
                });
                setSessionExpired(false);
                /**
                 * Trade the code in for a normal session token right away: a QR
                 * code or NFC card is a REUSABLE credential, but logout revokes
                 * whichever token it is holding, so leaving the code as the live
                 * token would burn the card on its owner's first sign-out. One
                 * shot only — a failed swap leaves the code working.
                 */
                await attemptRefresh();
                return null;
            } catch {
                // Offline — the QR token is server-minted and long-lived, so
                // decode it locally (same trust model as an online QR scan: the
                // QR is the credential). It gets DB-validated on the next sync.
                const localUser = userFromToken(token);
                if (localUser) {
                    setUser(localUser);
                    setSessionExpired(false);
                    return null;
                }
                restorePreviousToken();
                return intl.formatMessage({ id: 'auth.error.qrInvalid' });
            }
        },
        [intl, attemptRefresh]
    );

    useEffect(() => {
        // An NFC login card that this device cannot read in-app (an iPhone, or
        // any browser without Web NFC) arrives as a URL instead, with the token
        // in the fragment.
        const cardToken = takeNfcTokenFromUrl();
        const stored = getToken();
        const otherPerson = cardToken ? otherPersonsCard(cardToken) : null;

        if (cardToken && otherPerson) {
            /**
             * Somebody else's card on a device already in use: ask first. Fall
             * through to the normal path meanwhile, so the dialog opens over
             * their own session. Reacting to a card token that arrived in the
             * URL — an external event, not state derivable from what is
             * already rendered.
             */
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setPendingCard({ token: cardToken, incoming: otherPerson });
        } else if (cardToken) {
            // Nobody signed in, or the same person's card: just redeem it.
            loginWithToken(cardToken)
                .then(setCardLoginError)
                .finally(() => setLoading(false));
            return;
        }

        const token = stored;
        if (!token) {
            setLoading(false);
            return;
        }

        refreshToken()
            .then((outcome) => {
                if (outcome === 'ok') {
                    return;
                }
                if (outcome === 'expired') {
                    setSessionExpired(true);
                    setReplicationAuthActive(false);
                    return;
                }
                /**
                 * Transient (offline, network error, aborted request, server
                 * blip): keep the user working with the locally-decoded token
                 * rather than bouncing them to re-login. A later refresh will
                 * confirm the session or expire it for real. Only give up if the
                 * stored token can't even be decoded into a user.
                 */
                const localUser = userFromToken(token);
                if (localUser) {
                    setUser(localUser);
                } else {
                    setSessionExpired(true);
                    setReplicationAuthActive(false);
                }
            })
            .finally(() => setLoading(false));
    }, [refreshToken, loginWithToken]);

    /**
     * The Android app takes a card held to the device itself instead of letting
     * Android open its URL in the browser — at start-up or while running. Same
     * rules as a card in the address bar above.
     */
    useEffect(
        () =>
            onOpenedWithUrl((url) => {
                const opened = new URL(url, window.location.origin);
                if (opened.origin !== window.location.origin) {
                    return;
                }
                const cardToken = tokenFromHash(opened.hash);
                if (!cardToken) {
                    return;
                }
                const otherPerson = otherPersonsCard(cardToken);
                if (otherPerson) {
                    setPendingCard({ token: cardToken, incoming: otherPerson });
                } else {
                    void loginWithToken(cardToken).then(setCardLoginError);
                }
            }),
        [loginWithToken]
    );

    /**
     * Signing out in one window signs out in all of them. The token is one per
     * device, so a second tab left standing has no credential any more — on a
     * shared tablet it would show the previous person's app to the next one.
     *
     * Only the token DISAPPEARING is acted on: a token appearing means somebody
     * signed in elsewhere, not here. `external` keeps this tab's own
     * `clearToken` out of it, which would race its navigate.
     */
    useEffect(
        () =>
            subscribeToken((external) => {
                if (!external || !user) {
                    return;
                }
                const token = getToken();
                if (token) {
                    /**
                     * Somebody ELSE now, which is "Anmelden als" (or the way
                     * back) started in another window. This tab would go on
                     * showing the old person while its requests went out as the
                     * new one; a reload makes it the new person throughout.
                     */
                    if (userFromToken(token)?.id !== user.id) {
                        window.location.reload();
                    }
                    return;
                }
                setUser(null);
                setSessionEndCode(null);
                setSessionExpired(true);
                setReplicationAuthActive(false);
            }),
        [user]
    );

    useEffect(() => {
        if (!user) {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
            return;
        }

        refreshTimer.current = setInterval(() => {
            refreshToken().then((outcome) => {
                // Only a real rejection ends the session; a transient failure is
                // ignored and simply retried at the next interval.
                if (outcome === 'expired') {
                    setSessionExpired(true);
                    setReplicationAuthActive(false);
                }
            });
        }, REFRESH_INTERVAL);

        return () => {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
        };
    }, [user, refreshToken]);

    const login = useCallback(
        async (username: string, password: string): Promise<string | null> => {
            try {
                const res = await fetch('/api/auth/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, password })
                });

                const data = await res.json();

                if (!res.ok) {
                    const messageId = AUTH_ERROR_CODE_TO_ID[data.code];
                    if (messageId) {
                        return intl.formatMessage({ id: messageId });
                    }
                    return (
                        data.error ||
                        intl.formatMessage({ id: 'auth.error.failed' })
                    );
                }

                setToken(data.token);
                const authUser: AuthUser = {
                    id: data.user.id,
                    username: data.user.username,
                    displayName: data.user.displayName || data.user.username,
                    isAdmin: data.user.isAdmin === true
                };
                setUser(authUser);
                setSessionExpired(false);
                // Best-effort: cache an encrypted offline credential (keyed on
                // the password just entered) so this user can log in offline
                // later in the field. Never blocks or fails the online login.
                void cacheOfflineCredential(username, password, authUser).catch(
                    () => {}
                );
                return null;
            } catch {
                /**
                 * Server unreachable — fall back to an offline login using the
                 * credential cached on this device from a previous online login.
                 * A server-reachable wrong password returns above (res not ok)
                 * and never reaches here, so offline only kicks in when truly
                 * offline.
                 */
                const payload = await loadOfflineCredential(username, password);
                if (payload) {
                    setToken(payload.token);
                    setUser(payload.user);
                    setSessionExpired(false);
                    return null;
                }
                return intl.formatMessage({ id: 'auth.error.offline' });
            }
        },
        [intl]
    );

    /** Hand the device over to the card's owner, keeping the queue attributable. */
    const confirmSwitch = useCallback(async () => {
        if (!pendingCard) {
            return;
        }
        const outgoingToken = getToken();
        // Their unsynced edits must still reach the server as *them*, and this
        // device may not see a network again for hours — so keep the token that
        // proves who they are. Dropped again as soon as their queue drains.
        if (user && outgoingToken && hasPendingChanges(user.id)) {
            keepCredential(
                user.id,
                outgoingToken,
                user.username,
                user.displayName || user.username
            );
        }
        setPendingCard(null);
        setCardLoginError(await loginWithToken(pendingCard.token));
    }, [pendingCard, user, loginWithToken]);

    // Every local write is journalled against whoever is signed in right now.
    useEffect(() => {
        setCurrentAuthor(user?.id ?? null);
    }, [user]);

    // The push publishes anyone whose queued changes it refused to send under
    // the wrong name; we ask that person to sign in and release them.
    useEffect(
        () => subscribeSyncBlockers(() => setBlockers(getSyncBlockers())),
        []
    );

    // Prove a blocked user's identity so their queue can go up as them. This
    // deliberately does NOT touch the session — whoever is using the device
    // stays signed in; the token goes straight into the pending-credential
    // vault that the push reads.
    const authenticateForSync = useCallback(
        async (
            blocker: SyncBlocker,
            credential:
                | { kind: 'password'; username: string; password: string }
                | { kind: 'token'; token: string }
        ): Promise<string | null> => {
            try {
                let token: string;
                if (credential.kind === 'password') {
                    const res = await fetch('/api/auth/login', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            username: credential.username,
                            password: credential.password
                        })
                    });
                    const data = await res.json();
                    if (!res.ok) {
                        const messageId = AUTH_ERROR_CODE_TO_ID[data.code];
                        return messageId
                            ? intl.formatMessage({ id: messageId })
                            : data.error ||
                                  intl.formatMessage({
                                      id: 'auth.error.failed'
                                  });
                    }
                    token = data.token;
                } else {
                    // A card or QR code: confirm the server still honours it,
                    // otherwise we would vault a credential that cannot push.
                    const check = await fetchAs(
                        credential.token,
                        '/api/auth/me'
                    );
                    if (!check.ok) {
                        return intl.formatMessage({
                            id: 'auth.error.qrInvalid'
                        });
                    }
                    token = credential.token;
                }

                const identity = userFromToken(token);
                if (!identity || identity.id !== blocker.userId) {
                    // Signing in as somebody else does not release this queue —
                    // that is the whole point.
                    return intl.formatMessage(
                        { id: 'sync.blocked.wrongUser' },
                        { name: blocker.name }
                    );
                }

                keepCredential(
                    blocker.userId,
                    token,
                    blocker.username,
                    blocker.name
                );
                clearSyncBlocker(blocker.userId);
                return null;
            } catch {
                return intl.formatMessage({ id: 'auth.error.unreachable' });
            }
        },
        [intl]
    );

    /**
     * Become `userId`. The page reloads into their session, so nothing of the
     * administrator's — open dialogs, the replication, the ability — carries
     * over.
     */
    const impersonate = useCallback(
        async (userId: string, targetName: string): Promise<string | null> => {
            const adminToken = getToken();
            if (!user || !adminToken) {
                return intl.formatMessage({ id: 'auth.impersonate.error' });
            }
            let token: string;
            try {
                token = await requestImpersonationToken(userId);
            } catch (err) {
                return err instanceof Error
                    ? err.message
                    : intl.formatMessage({ id: 'auth.impersonate.error' });
            }
            setReplicationAuthActive(false);
            // The administrator's own queue goes on going up as them.
            if (hasPendingChanges(user.id)) {
                keepCredential(
                    user.id,
                    adminToken,
                    user.username,
                    user.displayName || user.username
                );
            }
            setImpersonation({ adminToken, targetName });
            setToken(token);
            window.location.assign('/');
            return null;
        },
        [user, intl]
    );

    /**
     * Back to the administrator. What was saved as the other person stays
     * theirs: with work still queued their token is kept until it is up, like
     * at a normal sign-out, and only revoked otherwise.
     */
    const endImpersonation = useCallback(async () => {
        const current = getImpersonation();
        setReplicationAuthActive(false);
        const token = getToken();
        if (user && token && hasPendingChanges(user.id)) {
            keepCredential(
                user.id,
                token,
                user.username,
                user.displayName || user.username
            );
        } else {
            await authFetch('/api/auth/logout', { method: 'POST' }).catch(
                () => {}
            );
        }
        setImpersonation(null);
        if (current) {
            setToken(current.adminToken);
        } else {
            clearToken();
        }
        window.location.assign('/office/admin/users');
    }, [user]);

    // The borrowed session ran out: the re-login prompt would ask for the
    // other person's password, so go back to the administrator instead.
    useEffect(() => {
        if (sessionExpired && impersonation) {
            void endImpersonation();
        }
    }, [sessionExpired, impersonation, endImpersonation]);

    const logout = useCallback(() => {
        // Signing out of somebody else's session is going back to one's own.
        if (impersonation) {
            void endImpersonation();
            return;
        }
        setReplicationAuthActive(false);
        const token = getToken();
        if (user && token && hasPendingChanges(user.id)) {
            /**
             * Signing off with work still queued. Revoking now would strand it —
             * the next user's push would be refused or filed under their name —
             * and offline the revoke never arrives at all, so the token would be
             * thrown away while the server still honours it. Keep it until the
             * queue drains and skip the revoke; it expires on its own schedule.
             */
            keepCredential(
                user.id,
                token,
                user.username,
                user.displayName || user.username
            );
        } else {
            authFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
        }
        clearToken();
        setUser(null);
        setSessionExpired(false);
        /**
         * Back to the root, because the login screen renders in PLACE of the
         * routes and leaves the address bar untouched: whoever signs in next
         * would otherwise land on the page the previous user left open — their
         * app, their project, their filters. From `/` the next login is routed
         * by its own roles again (see RootRedirect).
         */
        navigate('/', { replace: true });
    }, [user, navigate, impersonation, endImpersonation]);

    // One prompt at a time — several stuck users would just stack dialogs.
    const blockedUser = blockers.find(
        (blocker) => !dismissedBlockers.includes(blocker.userId)
    );

    // Here, not inside the routes: it has to stand over the login page and the
    // start-up screens as well (the test mark), and it is what knows the way
    // back from somebody else's session.
    const banner = (
        <TopEdgeBanner
            impersonation={impersonation}
            onEndImpersonation={() => void endImpersonation()}
        />
    );

    if (loading) {
        return (
            <>
                {banner}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh'
                    }}
                >
                    <CircularProgress />
                </Box>
            </>
        );
    }

    if (!user && !sessionExpired) {
        return (
            <>
                {banner}
                <LoginPage
                    login={login}
                    loginWithToken={loginWithToken}
                    initialError={cardLoginError}
                />
            </>
        );
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                loginWithToken,
                logout,
                sessionExpired,
                impersonation,
                impersonate,
                endImpersonation
            }}
        >
            {banner}
            {children}
            {pendingCard && user && (
                <SwitchUserDialog
                    currentName={user.displayName || user.username}
                    incomingName={
                        pendingCard.incoming.displayName ||
                        pendingCard.incoming.username
                    }
                    pendingCount={pendingCountsByAuthor().get(user.id) ?? 0}
                    onConfirm={() => void confirmSwitch()}
                    onCancel={() => setPendingCard(null)}
                />
            )}
            {blockedUser && (
                <SyncLoginDialog
                    blocker={blockedUser}
                    onAuthenticate={(credential) =>
                        authenticateForSync(blockedUser, credential)
                    }
                    onDismiss={() =>
                        setDismissedBlockers((ids) => [
                            ...ids,
                            blockedUser.userId
                        ])
                    }
                />
            )}
            {sessionExpired && !impersonation && (
                <ReLoginDialog
                    login={login}
                    loginWithToken={loginWithToken}
                    reasonId={
                        sessionEndCode
                            ? AUTH_ERROR_CODE_TO_ID[sessionEndCode]
                            : undefined
                    }
                />
            )}
        </AuthContext.Provider>
    );
}
